'use client'

import { useCallback, useState } from 'react'
import type { CartLine } from '@mist/contracts/commerce'
import {
  ATELIER_COPY,
  BIRTHSTONE_COLLECTION,
  COLLECTION_PRICE_USD,
  birthstoneTitle,
  type CollectionLine,
  type MonthKey,
} from '@mist/content/collection'
import { LANDING_SEQUENCE } from '@mist/content/route-content'
import { CART_ENGRAVING_MAX_LENGTH, CART_SIGNATURE_MAX_LENGTH } from '@mist/contracts/commerce'
import { collectionBottleMedia } from '@/lib/collection-media'
import { trackEvent } from '@/lib/analytics'
import AtelierBottleStage from '@/components/experience/AtelierBottleStage'
import StoneThumbnail from '@/components/experience/StoneThumbnail'
import AddToCartButton from '@/components/commerce/AddToCartButton'

const copy = LANDING_SEQUENCE.find((section) => section.id === 'collection')!

type Props = { initialMonth: MonthKey; initialLine: CollectionLine }

/**
 * The Birthstone Collection showcase. Selection state lives in the URL for the
 * no-JS path (every control is a real link to /?month=…&line=…#collection);
 * client-side, an unmodified primary click switches instantly via state +
 * History.replaceState, while modified clicks (⌘/ctrl/shift/alt, middle button)
 * fall through to the browser so links still open in a new tab or window.
 *
 * The card mirrors the family draft's display card: the live bottle on the
 * left; on the right a title row — "<Gem> — <Month>" (or "Pearl / Moonstone —
 * June" when the pictured stone differs from the bottle's gem) with the stone
 * thumbnail on the far edge — the verse, the engraving and box-signature
 * fields, and Add to Cart. The two fields are local state only: they are not in
 * a form, never reach the network here, and never enter analytics; they travel
 * only inside the bag line the visitor adds, which the checkout request later
 * carries to the API. Analytics records month/line choices and the add itself.
 */
export default function BirthstoneAtelier({ initialMonth, initialLine }: Props) {
  const [monthKey, setMonthKey] = useState<MonthKey>(initialMonth)
  const [line, setLine] = useState<CollectionLine>(initialLine)
  const [engraving, setEngraving] = useState('')
  const [signature, setSignature] = useState<string>(ATELIER_COPY.signatureDefault)

  const activeIndex = Math.max(
    0,
    BIRTHSTONE_COLLECTION.findIndex((entry) => entry.key === monthKey),
  )
  const active = BIRTHSTONE_COLLECTION[activeIndex]!
  const record = active[line]
  const media = collectionBottleMedia(record.image)
  const lineName = line === 'femme' ? 'Femme' : 'Homme'
  const title = birthstoneTitle(active)

  const select = useCallback(
    (nextMonth: MonthKey, nextLine: CollectionLine, action: 'month' | 'line') =>
      (event: React.MouseEvent<HTMLAnchorElement>) => {
        // Let the browser own modified clicks: open-in-new-tab/window must work.
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
        event.preventDefault()
        setMonthKey(nextMonth)
        setLine(nextLine)
        window.history.replaceState(null, '', `/?month=${nextMonth}&line=${nextLine}#collection`)
        trackEvent('mist_collection_interaction', {
          route: '/',
          collection_action: action,
          collection_month: nextMonth,
          collection_line: nextLine,
        })
      },
    [],
  )

  // What the bag receives. The store re-trims and re-validates, and the API
  // prices the line itself; the amount here is display only.
  const trimmedEngraving = engraving.trim()
  const trimmedSignature = signature.trim() || ATELIER_COPY.signatureDefault
  const cartLine: CartLine = {
    kind: 'bottle',
    month: active.key,
    line,
    signature: trimmedSignature,
    quantity: 1,
    ...(trimmedEngraving ? { engraving: trimmedEngraving } : {}),
  }
  const cartDetails = [
    ...(trimmedEngraving ? [`Engraving: "${trimmedEngraving}"`] : []),
    `Box signature: "${trimmedSignature}"`,
  ]

  return (
    <section
      className="chapter atelier"
      aria-labelledby="collection-title"
      id="collection"
      style={{ ['--atelier-glow' as string]: record.color }}
    >
      <div className="mist-shell atelier__inner">
        <header className="atelier__header">
          <p className="eyebrow">01 / {copy.title}</p>
          <h2 id="collection-title">{copy.title}</h2>
          <p className="measure lede atelier__lede">{copy.body[0]}</p>
        </header>

        <div className="atelier__index">
          <div className="atelier__index-head">
            <nav className="atelier__lines" aria-label="Collection line">
              {(['femme', 'homme'] as const).map((candidate) => (
                <a
                  key={candidate}
                  className="atelier__line-link"
                  aria-current={line === candidate ? 'true' : undefined}
                  href={`/?month=${monthKey}&line=${candidate}#collection`}
                  onClick={select(monthKey, candidate, 'line')}
                >
                  Pour {candidate === 'femme' ? 'Femme' : 'Homme'}
                </a>
              ))}
            </nav>
            <p className="atelier__index-label" id="collection-months-label">
              Birth month
            </p>
          </div>

          <nav className="atelier__months" aria-labelledby="collection-months-label">
            {BIRTHSTONE_COLLECTION.map((entry, index) => (
              <a
                key={entry.key}
                className="atelier__month"
                aria-current={entry.key === monthKey ? 'true' : undefined}
                href={`/?month=${entry.key}&line=${line}#collection`}
                onClick={select(entry.key, line, 'month')}
              >
                <span className="atelier__month-index" aria-hidden="true">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span
                  className="atelier__swatch"
                  aria-hidden="true"
                  style={{ ['--atelier-swatch' as string]: entry[line].color }}
                />
                <span className="atelier__month-name">{entry.month}</span>
                <span className="atelier__month-gem">{entry.gem}</span>
              </a>
            ))}
          </nav>
        </div>

        <div className="atelier__card">
          <AtelierBottleStage
            gem={active.gem}
            month={active.month}
            line={line}
            color={record.color}
            engraving={engraving}
            media={media}
            alt={`${active.gem} bottle — ${active.month}, Pour ${lineName}`}
          />
          <div className="atelier__details" key={`${active.key}-${line}`}>
            <p className="atelier__line-label">
              <span
                className="atelier__swatch atelier__swatch--record"
                aria-hidden="true"
                style={{ ['--atelier-swatch' as string]: record.color }}
              />
              Pour {lineName}
              <span className="atelier__record-index" aria-hidden="true">
                {String(activeIndex + 1).padStart(2, '0')} / 12
              </span>
            </p>
            <div className="atelier__title-row">
              <h3 className="atelier__gem">{title}</h3>
              <StoneThumbnail key={`${active.key}-${line}`} name={active.stone.name} image={active.stone.image} />
            </div>
            <blockquote className="atelier__poem">{record.poem}</blockquote>

            <div className="field atelier__field">
              <div className="atelier__field-head">
                <label htmlFor="engraving-preview">{ATELIER_COPY.engravingLabel}</label>
                <span className="atelier__field-count" aria-hidden="true">
                  {engraving.length}/{CART_ENGRAVING_MAX_LENGTH}
                </span>
              </div>
              <input
                id="engraving-preview"
                type="text"
                maxLength={CART_ENGRAVING_MAX_LENGTH}
                value={engraving}
                placeholder={ATELIER_COPY.engravingPlaceholder}
                autoComplete="off"
                onChange={(event) => setEngraving(event.target.value)}
              />
            </div>

            <div className="field atelier__field atelier__field--signature">
              <div className="atelier__field-head">
                <label htmlFor="box-signature">{ATELIER_COPY.signatureLabel}</label>
              </div>
              <input
                id="box-signature"
                type="text"
                maxLength={CART_SIGNATURE_MAX_LENGTH}
                value={signature}
                autoComplete="off"
                aria-describedby="box-signature-hint"
                data-box-signature=""
                onChange={(event) => setSignature(event.target.value)}
              />
              <p className="hint" id="box-signature-hint">
                {ATELIER_COPY.signatureHint}
              </p>
            </div>

            <AddToCartButton
              className="mist-button mist-button--primary atelier__add"
              line={cartLine}
              title={`${active.gem} (${active.month}) — Pour ${lineName}`}
              details={cartDetails}
              unitAmountCents={COLLECTION_PRICE_USD * 100}
              label={ATELIER_COPY.addToCart}
            />
          </div>
        </div>
        <p className="measure atelier__note">{copy.body[1]}</p>
      </div>
    </section>
  )
}
